import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, ShoppingCart, X } from 'lucide-react';
import SeoHead from './SeoHead';
import FAQSection from './FAQSection';

const faqItems = [
  { q: 'Do you provide event decor in Pretoria?', a: 'Yes, we do decor setups in Pretoria and wider Gauteng including Centurion, Midrand, and Johannesburg.' },
  { q: 'Can decor be combined with catering and sound?', a: 'Yes, we offer full-service event solutions and can bundle decor with other services.' },
  { q: 'Do you have decor packages for corporate events?', a: 'Yes, we provide both private and corporate decor options.' },
  { q: 'How is decor pricing calculated?', a: 'Pricing is based on guest count and package requirements, with clear quote confirmation.' },
  { q: 'How quickly can I book?', a: 'You can select your decor and send a WhatsApp request immediately. Limited availability applies on weekends.' },
];

const Decor = () => {
  const [selectedDecor, setSelectedDecor] = useState([]);
  const [showCart, setShowCart] = useState(false);

  // Price mapping based on guest count
  const priceMap = {
    '20': 'R8 500',
    '30': 'R12 750',
    '40': 'R17 500',
    '50': 'R21 750',
    '60': 'R26 500',
    '70': 'R30 750',
    '80': 'R35 500',
    '100': 'R44 500',
    '150': 'R66 250',
    '200': 'R89 500',
    '200+': 'Upon request'
  };

  // Guest options based on pricing table
  const guestOptions = [
    { value: '', label: 'Select guests' },
    { value: '20', label: '20 Guests' },
    { value: '30', label: '30 Guests' },
    { value: '40', label: '40 Guests' },
    { value: '50', label: '50 Guests' },
    { value: '60', label: '60 Guests' },
    { value: '70', label: '70 Guests' },
    { value: '80', label: '80 Guests' },
    { value: '100', label: '100 Guests' },
    { value: '150', label: '150 Guests' },
    { value: '200', label: '200 Guests' },
    { value: '200+', label: '200+ Guests' }
  ];

  // Load saved decor selections from localStorage
  useEffect(() => {
    const savedDecor = localStorage.getItem('ubuntu-events-management-decor-selections');
    if (savedDecor) {
      setSelectedDecor(JSON.parse(savedDecor));
    }
  }, []);

  // Save decor selections to localStorage
  useEffect(() => {
    localStorage.setItem('ubuntu-events-management-decor-selections', JSON.stringify(selectedDecor));
  }, [selectedDecor]);

  const decorCategories = [
    {
      id: 'weddings',
      name: 'Weddings',
      description: 'Elegant white & gold wedding setups',
      image: 'https://images.unsplash.com/photo-1519741497674-611481863552?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 'birthdays',
      name: 'Birthdays',
      description: 'Vibrant and colorful birthday themes',
      image: '/decor/birthday-1.png',
      images: ['/decor/birthday-1.png', '/decor/birthday-2.png'],
    },
    {
      id: 'corporate',
      name: 'Corporate Events',
      description: 'Professional setups for formal occasions',
      image: 'https://images.unsplash.com/photo-1511578314322-379afb476865?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 'outdoor',
      name: 'Outdoor Functions',
      description: 'Garden and picnic-style settings',
      image: 'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 'anniversaries',
      name: 'Anniversaries',
      description: 'Romantic and intimate celebration setups',
      image: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 'graduations',
      name: 'Graduations',
      description: 'Celebratory academic achievement themes',
      image: '/graduation.jpg'
    }
  ];

  const toggleDecorSelection = (decor) => {
    const isSelected = selectedDecor.some(item => item.id === decor.id);
    if (isSelected) {
      setSelectedDecor(selectedDecor.filter(item => item.id !== decor.id));
    } else {
      setSelectedDecor([...selectedDecor, { ...decor, numberOfGuests: '' }]);
    }
  };

  const updateGuestCount = (decorId, numberOfGuests) => {
    setSelectedDecor(prev => 
      prev.map(item => 
        item.id === decorId ? { ...item, numberOfGuests } : item
      )
    );
  };

  const getSelectedDecor = (decorId) => {
    return selectedDecor.find(item => item.id === decorId);
  };

  const getPrice = (numberOfGuests) => {
    return numberOfGuests ? priceMap[numberOfGuests] || 'Price not available' : null;
  };

  const parsePrice = (priceLabel) => {
    if (!priceLabel) return 0;
    const normalized = String(priceLabel).replace(/[^0-9]/g, '');
    return normalized ? parseInt(normalized, 10) : 0;
  };

  const getDecorTotal = () =>
    selectedDecor.reduce((total, item) => {
      const currentPrice = getPrice(item.numberOfGuests);
      if (!currentPrice || currentPrice === 'Upon request' || currentPrice === 'Price not available') {
        return total;
      }
      return total + parsePrice(currentPrice);
    }, 0);

  const hasQuoteOnlyItems = selectedDecor.some((item) => getPrice(item.numberOfGuests) === 'Upon request');

  const removeDecorSelection = (decorId) => {
    setSelectedDecor((prev) => prev.filter((item) => item.id !== decorId));
  };

  const buildWhatsAppListMessage = () => {
    const lines = selectedDecor.map((item, idx) => {
      const currentPrice = getPrice(item.numberOfGuests);
      return `${idx + 1}. ${item.name}
   Guests: ${item.numberOfGuests || 'Not specified'}
   Price: ${currentPrice || 'Not available'}`;
    });

    const total = getDecorTotal();
    const totalLine = hasQuoteOnlyItems
      ? `Estimated subtotal (excluding quote-only items): R${total.toLocaleString()}`
      : `Estimated total: R${total.toLocaleString()}`;

    return `Hi UBUNTU EVENTS MANAGEMENT,

I'd like a quote for the following decor selections:

${lines.join('\n\n')}

${totalLine}

Please confirm availability and final pricing.`;
  };

  const getDecorGallerySources = (decor) => {
    if (decor.images?.length) return decor.images;
    return decor.image ? [decor.image] : [];
  };

  const DecorCardMedia = ({ decor }) => {
    const sources = getDecorGallerySources(decor);
    const primary = sources[0];
    if (!primary) return null;

    if (sources.length <= 1) {
      return (
        <div className="aspect-w-16 aspect-h-9 mb-4 overflow-hidden rounded-lg">
          <img
            src={primary}
            alt={`${decor.name} event decor Gauteng`}
            className="w-full h-56 sm:h-64 object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
      );
    }

    return (
      <div className="mb-4" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <div
          className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth rounded-lg border border-gray-100 shadow-sm [scrollbar-width:thin]"
          style={{ WebkitOverflowScrolling: 'touch' }}
          role="region"
          aria-roledescription="carousel"
          aria-label={`${decor.name} gallery — scroll sideways`}
        >
          {sources.map((src, i) => (
            <figure
              key={src}
              className="min-w-full shrink-0 snap-center snap-always m-0"
            >
              <img
                src={src}
                alt={`${decor.name} styling Gauteng — ${i + 1} of ${sources.length}`}
                className="w-full h-56 sm:h-64 object-cover select-none"
                draggable={false}
              />
            </figure>
          ))}
        </div>
        <p className="mt-2 text-center text-[11px] font-medium tracking-wide text-gray-500 uppercase">
          Scroll or swipe for more
        </p>
      </div>
    );
  };

  const sendWhatsAppMessage = () => {
    if (!selectedDecor.length) return;
    const encodedMessage = encodeURIComponent(buildWhatsAppListMessage());
    const whatsappUrl = `https://wa.me/27663286231?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className={`min-h-screen py-8 px-4 ${selectedDecor.length > 0 ? 'pb-32 sm:pb-28' : ''}`}>
      <SeoHead
        title="Event Decor Pretoria | Ubuntu Enterprises"
        description="Event decor in Pretoria and Gauteng with guest-based package pricing and quick WhatsApp booking."
        canonicalPath="/decor"
        faqSchema={faqItems}
      />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-playfair font-bold text-gray-800 mb-4">
            Decor Gallery
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Transform your space with our elegant decor packages. 
            Each setup is carefully crafted to create the perfect atmosphere for your special occasion.
          </p>
        </motion.div>

        {/* What's Included Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mb-12"
        >
          <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-gold/20">
            <h2 className="text-2xl font-playfair font-bold text-gray-800 mb-4 text-center">
              All Packages Include
            </h2>
            <p className="text-center text-gray-600 mb-6 text-sm italic">
              Every decor package comes with the following items:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
              <div className="flex items-center space-x-3 p-3 bg-gold/5 rounded-lg">
                <div className="w-2 h-2 bg-gold rounded-full"></div>
                <span className="text-gray-700 font-medium">Programs</span>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-gold/5 rounded-lg">
                <div className="w-2 h-2 bg-gold rounded-full"></div>
                <span className="text-gray-700 font-medium">Welcome board</span>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-gold/5 rounded-lg">
                <div className="w-2 h-2 bg-gold rounded-full"></div>
                <span className="text-gray-700 font-medium">Table numbers</span>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-gold/5 rounded-lg">
                <div className="w-2 h-2 bg-gold rounded-full"></div>
                <span className="text-gray-700 font-medium">Fresh Flowers</span>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-gold/5 rounded-lg">
                <div className="w-2 h-2 bg-gold rounded-full"></div>
                <span className="text-gray-700 font-medium">Pure Water</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Decor Grid */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12">
          {decorCategories.map((decor, index) => (
            <motion.div
              key={decor.id}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className={`card group cursor-pointer transition-all duration-300 ${
                selectedDecor.some(item => item.id === decor.id)
                  ? 'ring-2 ring-gold bg-gold/5'
                  : 'hover:shadow-2xl'
              }`}
              onClick={() => toggleDecorSelection(decor)}
            >
              <DecorCardMedia decor={decor} />
              
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-xl font-playfair font-semibold text-gray-800">
                  {decor.name}
                </h3>
                {getSelectedDecor(decor.id)?.numberOfGuests ? (
                  <span className="text-lg font-bold text-gold">
                    {getPrice(getSelectedDecor(decor.id).numberOfGuests)}
                  </span>
                ) : (
                  <span className="text-lg font-bold text-gold">
                    From R8 500
                  </span>
                )}
              </div>
              
              <p className="text-gray-600 mb-4 text-sm">
                {decor.description}
              </p>
              <p className="text-xs text-gray-500 mb-3 italic">
                Pricing based on guest count - see table below
              </p>
              
              {/* Number of Guests Selector */}
              <div className="mb-3" onClick={(e) => e.stopPropagation()}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Number of Guests
                </label>
                <select
                  value={getSelectedDecor(decor.id)?.numberOfGuests || ''}
                  onChange={(e) => {
                    // Auto-select the decor if not already selected when user selects guests
                    if (!selectedDecor.some(item => item.id === decor.id)) {
                      toggleDecorSelection(decor);
                    }
                    updateGuestCount(decor.id, e.target.value);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent bg-white text-gray-700 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  onClick={(e) => e.stopPropagation()}
                >
                  {guestOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {/* Display Price after selection */}
                {getSelectedDecor(decor.id)?.numberOfGuests && (
                  <div className="mt-2 p-2 bg-gold/10 rounded-lg">
                    <p className="text-sm font-semibold text-gray-700">
                      Price: <span className="text-gold font-bold">{getPrice(getSelectedDecor(decor.id).numberOfGuests)}</span>
                    </p>
                  </div>
                )}
              </div>
              
              <button
                className={`w-full py-2 px-4 rounded-lg font-semibold transition-colors duration-200 ${
                  selectedDecor.some(item => item.id === decor.id)
                    ? 'bg-gold text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gold hover:text-white'
                }`}
              >
                {selectedDecor.some(item => item.id === decor.id) ? 'Selected' : 'Select This Decor'}
              </button>
            </motion.div>
          ))}
        </div>

        {/* Pricing Table */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-12"
        >
          <div className="card bg-gradient-to-br from-gold/10 to-olive/10 border-2 border-gold/20">
            <h2 className="text-3xl font-playfair font-bold text-gray-800 mb-6 text-center">
              UBUNTU EVENTS MANAGEMENT DECO AND PRICES
            </h2>
            <p className="text-center text-gray-600 mb-8 font-semibold">
              PRICE LIST FOR 2026
            </p>
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gold/20">
                    <th className="border border-gold/30 px-6 py-4 text-left font-bold text-gray-800">Number of Guests</th>
                    <th className="border border-gold/30 px-6 py-4 text-left font-bold text-gray-800">Price</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="hover:bg-gold/5 transition-colors">
                    <td className="border border-gold/30 px-6 py-4 font-semibold text-gray-700">20 Guest</td>
                    <td className="border border-gold/30 px-6 py-4 text-gold font-bold">R8 500</td>
                  </tr>
                  <tr className="hover:bg-gold/5 transition-colors">
                    <td className="border border-gold/30 px-6 py-4 font-semibold text-gray-700">30 Guest</td>
                    <td className="border border-gold/30 px-6 py-4 text-gold font-bold">R12 750</td>
                  </tr>
                  <tr className="hover:bg-gold/5 transition-colors">
                    <td className="border border-gold/30 px-6 py-4 font-semibold text-gray-700">40 Guest</td>
                    <td className="border border-gold/30 px-6 py-4 text-gold font-bold">R17 500</td>
                  </tr>
                  <tr className="hover:bg-gold/5 transition-colors">
                    <td className="border border-gold/30 px-6 py-4 font-semibold text-gray-700">50 Guest</td>
                    <td className="border border-gold/30 px-6 py-4 text-gold font-bold">R21 750</td>
                  </tr>
                  <tr className="hover:bg-gold/5 transition-colors">
                    <td className="border border-gold/30 px-6 py-4 font-semibold text-gray-700">60 Guest</td>
                    <td className="border border-gold/30 px-6 py-4 text-gold font-bold">R26 500</td>
                  </tr>
                  <tr className="hover:bg-gold/5 transition-colors">
                    <td className="border border-gold/30 px-6 py-4 font-semibold text-gray-700">70 Guest</td>
                    <td className="border border-gold/30 px-6 py-4 text-gold font-bold">R30 750</td>
                  </tr>
                  <tr className="hover:bg-gold/5 transition-colors">
                    <td className="border border-gold/30 px-6 py-4 font-semibold text-gray-700">80 Guest</td>
                    <td className="border border-gold/30 px-6 py-4 text-gold font-bold">R35 500</td>
                  </tr>
                  <tr className="hover:bg-gold/5 transition-colors">
                    <td className="border border-gold/30 px-6 py-4 font-semibold text-gray-700">100 Guest</td>
                    <td className="border border-gold/30 px-6 py-4 text-gold font-bold">R44 500</td>
                  </tr>
                  <tr className="hover:bg-gold/5 transition-colors">
                    <td className="border border-gold/30 px-6 py-4 font-semibold text-gray-700">150 Guest</td>
                    <td className="border border-gold/30 px-6 py-4 text-gold font-bold">R66 250</td>
                  </tr>
                  <tr className="hover:bg-gold/5 transition-colors bg-gold/10">
                    <td className="border border-gold/30 px-6 py-4 font-semibold text-gray-700">200 Guest</td>
                    <td className="border border-gold/30 px-6 py-4 text-gold font-bold">R89 500</td>
                  </tr>
                  <tr className="hover:bg-gold/5 transition-colors">
                    <td className="border border-gold/30 px-6 py-4 font-semibold text-gray-700">200+ Guest</td>
                    <td className="border border-gold/30 px-6 py-4 text-gray-600 italic">Upon request</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mt-16 text-center bg-gradient-to-r from-gold/10 to-olive/10 rounded-xl p-8"
        >
          <h3 className="text-2xl font-playfair font-bold text-gray-800 mb-4">
            Don't see what you're looking for?
          </h3>
          <p className="text-gray-600 mb-6">
            We offer custom decor packages tailored to your specific needs and vision. 
            Contact us to discuss your unique requirements.
          </p>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'contact' }))}
            className="btn-primary"
          >
            Contact Us for Custom Decor
          </button>
        </motion.div>
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mt-10 rounded-xl border border-gold/20 bg-white p-8"
        >
          <h3 className="text-2xl font-playfair font-bold text-gray-800 mb-3">
            Our Other Services
          </h3>
          <p className="text-gray-600 mb-6">
            Complete your event with trusted catering, quality sound setup, and direct access to our homepage.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'menu' }))}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-800 hover:border-gold hover:bg-gold/5"
            >
              Professional Event Catering
            </button>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'soundhire' }))}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-800 hover:border-gold hover:bg-gold/5"
            >
              Sound Equipment Hire for Events
            </button>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'home' }))}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-800 hover:border-gold hover:bg-gold/5 sm:col-span-2"
            >
              Return to Homepage
            </button>
          </div>
        </motion.section>
        <FAQSection title="Decor FAQs" faqs={faqItems} />
      </div>

      {selectedDecor.length > 0 && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="fixed bottom-0 left-0 right-0 z-40 border-t border-black/15 bg-[#f5f0e6]/95 backdrop-blur-sm shadow-[0_-4px_24px_rgba(0,0,0,0.08)] pb-[max(0.75rem,env(safe-area-inset-bottom))]"
        >
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <button
              type="button"
              onClick={() => setShowCart(true)}
              className="flex items-center gap-3 text-left text-black min-w-0 flex-1"
            >
              <span className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#2a2a2a] text-[#f5f0e6]">
                <ShoppingCart size={20} />
                <span className="absolute -top-1 -right-1 flex h-5 min-w-[1.25rem] px-1 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white">
                  {selectedDecor.length}
                </span>
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-black">
                  {selectedDecor.length} selection{selectedDecor.length !== 1 ? 's' : ''} · {hasQuoteOnlyItems ? `from R${getDecorTotal().toLocaleString()}` : `R${getDecorTotal().toLocaleString()}`}
                </span>
                <span className="block text-xs text-black/55 truncate">
                  Tap to review, edit guests, or send the full list to WhatsApp
                </span>
              </span>
            </button>
            <div className="flex gap-2 shrink-0 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setShowCart(true)}
                className="flex-1 sm:flex-none rounded-lg border border-black/20 bg-white/60 px-4 py-2.5 text-sm font-semibold text-black hover:bg-white/90"
              >
                Review
              </button>
              <button
                type="button"
                onClick={sendWhatsAppMessage}
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-green-700"
              >
                <MessageCircle size={18} />
                WhatsApp
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {showCart && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#f5f0e6] rounded-t-2xl sm:rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto border border-black/10 shadow-xl"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-black">Your decor selection</h3>
              <button
                type="button"
                onClick={() => setShowCart(false)}
                className="text-black/50 hover:text-black"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              {selectedDecor.map((item) => (
                <div key={item.id} className="border-b border-black/10 pb-3">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <h4 className="font-semibold text-sm text-black">{item.name}</h4>
                      <p className="text-xs text-black/60">{item.description}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeDecorSelection(item.id)}
                      className="text-red-600 text-xs font-semibold"
                    >
                      Remove
                    </button>
                  </div>
                  <div className="flex items-center gap-3">
                    <select
                      value={item.numberOfGuests || ''}
                      onChange={(e) => updateGuestCount(item.id, e.target.value)}
                      className="flex-1 p-2 border border-black/20 rounded-lg bg-white/70 text-sm"
                    >
                      {guestOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <span className="text-sm font-semibold text-gold min-w-[88px] text-right">
                      {getPrice(item.numberOfGuests) || 'No price'}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-lg border border-black/10 bg-white/40 p-3 mb-4 max-h-40 overflow-y-auto text-xs text-black/80 whitespace-pre-wrap font-mono leading-relaxed">
              {buildWhatsAppListMessage()}
            </div>

            <div className="border-t border-black/15 pt-4 mb-4">
              <div className="flex justify-between text-lg font-bold text-black">
                <span>Total:</span>
                <span>{hasQuoteOnlyItems ? `from R${getDecorTotal().toLocaleString()}` : `R${getDecorTotal().toLocaleString()}`}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={sendWhatsAppMessage}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg flex items-center justify-center space-x-2"
            >
              <MessageCircle size={20} />
              <span>Send full list to WhatsApp</span>
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Decor;
